(function () {
  'use strict';

  var COUNT_MAP_VERSION = 'rgb444-ciede2000-v1';
  var COUNT_MAP_CACHE_TTL = 60 * 60 * 1000;
  var COUNT_DEBOUNCE_MS = 280;
  var DEFAULT_HEX = '#4F74B8';
  var SPECTRUM_STOPS = [
    [0, '#D91F2B'],
    [.083, '#FF762C'],
    [.167, '#F3DC32'],
    [.333, '#47D34F'],
    [.5, '#25D9D5'],
    [.61, '#4F74B8'],
    [.667, '#3235DF'],
    [.75, '#8429DC'],
    [.889, '#E71A95'],
    [1, '#D91F2B']
  ];
  var COLOUR_FAMILY_HEX = {
    black: '#111111',
    white: '#F7F7F4',
    grey: '#888B8A',
    gray: '#888B8A',
    blue: '#2455D6',
    navy: '#182644',
    red: '#D92F2F',
    pink: '#EF9DB8',
    green: '#3B8B42',
    purple: '#7B4B9E',
    yellow: '#F2DF31',
    orange: '#E98B2A',
    brown: '#86563B',
    beige: '#DFCFAC',
    cream: '#F1EAD8',
    ivory: '#F1EAD8',
    gold: '#C7A441',
    silver: '#C8C8C8',
    khaki: '#8C8A59',
    burgundy: '#6D1F2B',
    maroon: '#6D1F2B',
    turquoise: '#39A7A7',
    teal: '#287D7A'
  };
  var countMapRequests = {};

  function clamp(value, minimum, maximum) {
    return Math.max(minimum, Math.min(maximum, value));
  }

  function componentHex(value) {
    return clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0');
  }

  function rgbHex(red, green, blue) {
    return ('#' + componentHex(red) + componentHex(green) + componentHex(blue)).toUpperCase();
  }

  function hexRgb(hex) {
    return {
      r: parseInt(hex.slice(1, 3), 16),
      g: parseInt(hex.slice(3, 5), 16),
      b: parseInt(hex.slice(5, 7), 16)
    };
  }

  function contrastInk(hex) {
    var rgb = hexRgb(hex);
    var brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    return brightness > 154 ? '#10231D' : '#FFFFFF';
  }

  function rgbHsl(red, green, blue) {
    red /= 255;
    green /= 255;
    blue /= 255;
    var maximum = Math.max(red, green, blue);
    var minimum = Math.min(red, green, blue);
    var delta = maximum - minimum;
    var hue = 0;
    var lightness = (maximum + minimum) / 2;
    var saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
    if (delta) {
      if (maximum === red) hue = 60 * (((green - blue) / delta) % 6);
      else if (maximum === green) hue = 60 * (((blue - red) / delta) + 2);
      else hue = 60 * (((red - green) / delta) + 4);
    }
    return { h: hue < 0 ? hue + 360 : hue, s: saturation, l: lightness };
  }

  function hslHex(hue, saturation, lightness) {
    var h = ((Number(hue) || 0) % 360 + 360) % 360;
    var s = clamp(Number(saturation) || 0, 0, 100) / 100;
    var l = clamp(Number(lightness) || 0, 0, 100) / 100;
    var chroma = (1 - Math.abs(2 * l - 1)) * s;
    var segment = h / 60;
    var secondary = chroma * (1 - Math.abs(segment % 2 - 1));
    var red = 0;
    var green = 0;
    var blue = 0;
    if (segment < 1) { red = chroma; green = secondary; }
    else if (segment < 2) { red = secondary; green = chroma; }
    else if (segment < 3) { green = chroma; blue = secondary; }
    else if (segment < 4) { green = secondary; blue = chroma; }
    else if (segment < 5) { red = secondary; blue = chroma; }
    else { red = chroma; blue = secondary; }
    var match = l - chroma / 2;
    return rgbHex((red + match) * 255, (green + match) * 255, (blue + match) * 255);
  }

  // Mirrors the established Find Your Colour naming bands. The blue boundary
  // includes the approved #4F74B8 default so the label remains Denim Blue.
  function friendlyName(hex) {
    var rgb = hexRgb(hex);
    var hsl = rgbHsl(rgb.r, rgb.g, rgb.b);
    if (hsl.l < .13) return 'Black';
    if (hsl.l > .92 && hsl.s < .12) return 'White';
    if (hsl.s < .1) return hsl.l < .35 ? 'Charcoal' : hsl.l < .72 ? 'Grey' : 'Silver';
    if (hsl.h < 15 || hsl.h >= 345) return hsl.l < .38 ? 'Burgundy' : 'Red';
    if (hsl.h < 42) return hsl.l > .72 ? 'Peach' : 'Orange';
    if (hsl.h < 68) return hsl.l > .8 ? 'Cream' : 'Yellow';
    if (hsl.h < 85) return 'Lime';
    if (hsl.h < 165) return hsl.l < .32 ? 'Forest Green' : 'Green';
    if (hsl.h < 195) return 'Turquoise';
    if (hsl.h < 226) return hsl.l > .68 ? 'Sky Blue' : 'Denim Blue';
    if (hsl.h < 252) return hsl.l < .32 ? 'Navy' : 'Royal Blue';
    if (hsl.h < 285) return 'Purple';
    if (hsl.h < 330) return hsl.l > .66 ? 'Pink' : 'Magenta';
    return 'Burgundy';
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function rgbLab(red, green, blue) {
    var rgb = [red / 255, green / 255, blue / 255];
    for (var channel = 0; channel < 3; channel++) {
      rgb[channel] = rgb[channel] > .04045
        ? Math.pow((rgb[channel] + .055) / 1.055, 2.4)
        : rgb[channel] / 12.92;
    }
    var x = (rgb[0] * .4124564 + rgb[1] * .3575761 + rgb[2] * .1804375) / .95047;
    var y = rgb[0] * .2126729 + rgb[1] * .7151522 + rgb[2] * .072175;
    var z = (rgb[0] * .0193339 + rgb[1] * .119192 + rgb[2] * .9503041) / 1.08883;
    function transform(value) {
      return value > .008856451679
        ? Math.pow(value, 1 / 3)
        : (7.787037037 * value) + (16 / 116);
    }
    var fx = transform(x), fy = transform(y), fz = transform(z);
    return [(116 * fy) - 16, 500 * (fx - fy), 200 * (fy - fz)];
  }

  function labDistance(left, right) {
    return Math.sqrt(
      Math.pow(left[0] - right[0], 2) +
      Math.pow(left[1] - right[1], 2) +
      Math.pow(left[2] - right[2], 2)
    );
  }

  // Keep this sampler in step with the main Find Your Colour photo tool so a
  // header upload produces the same twelve suggestions and coverage figures.
  function dominantPhotoColours(canvas) {
    var width = canvas.width, height = canvas.height;
    if (width < 2 || height < 2) return [];
    var pixels = canvas.getContext('2d', { willReadFrequently: true })
      .getImageData(0, 0, width, height).data;

    function pixel(x, y) {
      var offset = ((y * width) + x) * 4;
      return [pixels[offset], pixels[offset + 1], pixels[offset + 2], pixels[offset + 3]];
    }

    var insetX = Math.min(2, Math.max(0, width - 1));
    var insetY = Math.min(2, Math.max(0, height - 1));
    var corners = [
      pixel(insetX, insetY),
      pixel(Math.max(0, width - 1 - insetX), insetY),
      pixel(insetX, Math.max(0, height - 1 - insetY)),
      pixel(Math.max(0, width - 1 - insetX), Math.max(0, height - 1 - insetY))
    ];
    var backgroundRgb = [0, 0, 0];
    corners.forEach(function (corner) {
      backgroundRgb[0] += corner[0];
      backgroundRgb[1] += corner[1];
      backgroundRgb[2] += corner[2];
    });
    var background = rgbLab(
      backgroundRgb[0] / 4,
      backgroundRgb[1] / 4,
      backgroundRgb[2] / 4
    );
    var backgroundLooksPlain = background[0] > 72 &&
      Math.sqrt(background[1] * background[1] + background[2] * background[2]) < 22;
    var step = Math.max(1, Math.floor(Math.max(width, height) / 48));
    var all = [], foreground = [];

    for (var y = 0; y < height; y += step) {
      for (var x = 0; x < width; x += step) {
        var rgba = pixel(x, y);
        if (rgba[3] < 35) continue;
        var lab = rgbLab(rgba[0], rgba[1], rgba[2]);
        var point = [lab[0], lab[1], lab[2], rgba[0], rgba[1], rgba[2], x, y];
        all.push(point);
        if (!backgroundLooksPlain || labDistance(lab, background) >= 8) foreground.push(point);
      }
    }

    var points = foreground.length >= Math.max(60, Math.floor(all.length * .08))
      ? foreground : all;
    if (points.length < 20) return [];
    var sorted = points.slice().sort(function (left, right) { return left[0] - right[0]; });
    var centres = [sorted[Math.floor(sorted.length / 2)].slice()];

    while (centres.length < 12) {
      var best = null, bestDistance = -1;
      points.forEach(function (point) {
        var nearest = Infinity;
        centres.forEach(function (centre) {
          nearest = Math.min(nearest, labDistance(point, centre));
        });
        if (nearest > bestDistance) {
          bestDistance = nearest;
          best = point;
        }
      });
      if (!best || bestDistance < 3) break;
      centres.push(best.slice());
    }

    var assignments = [];
    for (var iteration = 0; iteration < 6; iteration++) {
      var sums = centres.map(function () { return [0, 0, 0, 0, 0, 0, 0]; });
      points.forEach(function (point, pointIndex) {
        var nearestIndex = 0, nearestDistance = Infinity;
        centres.forEach(function (centre, centreIndex) {
          var distance = labDistance(point, centre);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = centreIndex;
          }
        });
        assignments[pointIndex] = nearestIndex;
        for (var channel = 0; channel < 6; channel++) sums[nearestIndex][channel] += point[channel];
        sums[nearestIndex][6] += 1;
      });
      centres.forEach(function (centre, centreIndex) {
        if (sums[centreIndex][6] < 1) return;
        for (var channel = 0; channel < 6; channel++) {
          centre[channel] = sums[centreIndex][channel] / sums[centreIndex][6];
        }
      });
    }

    return centres.map(function (centre, centreIndex) {
      var members = assignments.filter(function (assignment) {
        return assignment === centreIndex;
      }).length;
      if (members < Math.max(4, Math.floor(points.length * .0075))) return null;
      var representative = null, representativeDistance = Infinity;
      points.forEach(function (point, pointIndex) {
        if (assignments[pointIndex] !== centreIndex) return;
        var distance = labDistance(point, centre);
        if (distance < representativeDistance) {
          representativeDistance = distance;
          representative = point;
        }
      });
      return {
        hex: rgbHex(centre[3], centre[4], centre[5]),
        coverage: members / Math.max(1, points.length),
        x: representative ? representative[6] / Math.max(1, width - 1) : .5,
        y: representative ? representative[7] / Math.max(1, height - 1) : .5
      };
    }).filter(function (cluster) {
      return !!cluster;
    }).sort(function (left, right) {
      return right.coverage - left.coverage;
    }).slice(0, 12);
  }

  function validColourCountMap(payload) {
    return !!payload && payload.source === 'preworn_visual_colour_count_map' &&
      payload.version === COUNT_MAP_VERSION && Number(payload.steps) === 16 &&
      Array.isArray(payload.counts) && payload.counts.length === 4096;
  }

  function colourCountMapIndex(hex, steps) {
    var rgb = hexRgb(hex);
    var maximum = Math.max(1, Number(steps) - 1);
    var redCell = Math.round(rgb.r * maximum / 255);
    var greenCell = Math.round(rgb.g * maximum / 255);
    var blueCell = Math.round(rgb.b * maximum / 255);
    return redCell * steps * steps + greenCell * steps + blueCell;
  }

  function colourCountMapMatch(payload, hex) {
    if (!validColourCountMap(payload)) return null;
    var index = colourCountMapIndex(hex, Number(payload.steps));
    var count = Number(payload.counts[index]);
    if (!isFinite(count) || count < 0) return null;
    return {
      count: Math.round(count),
      capped: Number(payload.maximumCount) > 0 && count >= Number(payload.maximumCount)
    };
  }

  function cacheKey(endpoint) {
    return 'preworn:colour-count-map:v1:' + String(endpoint || '').split('?')[0];
  }

  function readCountMap(endpoint) {
    try {
      if (!window.localStorage) return null;
      var record = JSON.parse(window.localStorage.getItem(cacheKey(endpoint)) || 'null');
      if (!record || !validColourCountMap(record.payload) ||
          Date.now() - Number(record.savedAt || 0) > COUNT_MAP_CACHE_TTL) return null;
      return record.payload;
    } catch (error) {
      return null;
    }
  }

  function writeCountMap(endpoint, payload) {
    if (!validColourCountMap(payload)) return;
    try {
      if (!window.localStorage) return;
      window.localStorage.setItem(cacheKey(endpoint), JSON.stringify({
        savedAt: Date.now(),
        payload: payload
      }));
    } catch (error) {}
  }

  function loadCountMap(endpoint) {
    var cached = readCountMap(endpoint);
    if (cached) return Promise.resolve(cached);
    if (countMapRequests[endpoint]) return countMapRequests[endpoint];
    if (!endpoint || typeof window.fetch !== 'function') return Promise.resolve(null);
    var url = new URL(endpoint, window.location.href);
    url.search = new URLSearchParams({ action: 'colour_count_map' }).toString();
    countMapRequests[endpoint] = window.fetch(url.toString(), {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      cache: 'force-cache',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('colour_count_map_failed');
      return response.json();
    }).then(function (payload) {
      if (!validColourCountMap(payload)) throw new Error('colour_count_map_invalid');
      writeCountMap(endpoint, payload);
      return payload;
    }).catch(function () {
      return null;
    });
    return countMapRequests[endpoint];
  }

  function resultUrl(path, hex) {
    var url = new URL(path || '/pages/find-your-colour', window.location.origin);
    url.searchParams.set('colour_hex', String(hex || DEFAULT_HEX).toUpperCase());
    url.searchParams.set('colour_tolerance', '12');
    return url.pathname + url.search;
  }

  function hueFromHex(hex) {
    var rgb = hexRgb(hex);
    return rgbHsl(rgb.r, rgb.g, rgb.b).h;
  }

  function spectrumHex(position) {
    var ratio = clamp(Number(position) || 0, 0, 1);
    for (var index = 1; index < SPECTRUM_STOPS.length; index++) {
      var right = SPECTRUM_STOPS[index];
      if (ratio > right[0]) continue;
      var left = SPECTRUM_STOPS[index - 1];
      var distance = right[0] - left[0];
      var amount = distance > 0 ? (ratio - left[0]) / distance : 0;
      var leftRgb = hexRgb(left[1]);
      var rightRgb = hexRgb(right[1]);
      return rgbHex(
        leftRgb.r + (rightRgb.r - leftRgb.r) * amount,
        leftRgb.g + (rightRgb.g - leftRgb.g) * amount,
        leftRgb.b + (rightRgb.b - leftRgb.b) * amount
      );
    }
    return SPECTRUM_STOPS[SPECTRUM_STOPS.length - 1][1];
  }

  function spectrumPosition(hex) {
    var value = String(hex || '').toUpperCase();
    for (var index = 0; index < SPECTRUM_STOPS.length; index++) {
      if (SPECTRUM_STOPS[index][1] === value) return SPECTRUM_STOPS[index][0];
    }
    return hueFromHex(value) / 359;
  }

  function selectionFromHue(hue) {
    var normalised = clamp(Number(hue) || 0, 0, 359);
    var hex = spectrumHex(normalised / 359);
    return { hue: normalised, hex: hex, name: friendlyName(hex) };
  }

  function familyHueRange(value) {
    var key = String(value || '').trim().toLowerCase();
    var ranges = {
      red: { start: 345, end: 375 }, pink: { start: 315, end: 375 },
      purple: { start: 250, end: 315 }, blue: { start: 195, end: 250 },
      navy: { start: 215, end: 250 }, green: { start: 85, end: 165 },
      turquoise: { start: 165, end: 195 }, teal: { start: 165, end: 205 },
      yellow: { start: 42, end: 68 }, orange: { start: 15, end: 42 },
      brown: { start: 10, end: 35 }, beige: { start: 25, end: 50 },
      cream: { start: 35, end: 60 }, ivory: { start: 35, end: 60 },
      gold: { start: 35, end: 55 }, khaki: { start: 45, end: 80 },
      burgundy: { start: 330, end: 360 }, maroon: { start: 330, end: 360 },
      black: { start: 0, end: 100, neutral: true },
      white: { start: 0, end: 100, neutral: true },
      grey: { start: 0, end: 100, neutral: true },
      gray: { start: 0, end: 100, neutral: true },
      silver: { start: 0, end: 100, neutral: true }
    };
    return ranges[key] || null;
  }

  function hueForFamilyRange(hue, range) {
    var normalised = ((Number(hue) || 0) % 360 + 360) % 360;
    if (!range || range.neutral) return normalised;
    var midpoint = (range.start + range.end) / 2;
    var candidates = [normalised - 360, normalised, normalised + 360];
    var closest = candidates[0];
    candidates.forEach(function (candidate) {
      if (Math.abs(candidate - midpoint) < Math.abs(closest - midpoint)) closest = candidate;
    });
    return clamp(closest, range.start, range.end);
  }

  function familyGradient(range) {
    if (!range) return '';
    if (range.neutral) {
      return 'linear-gradient(90deg, #f7f7f4 0%, #b7b9b7 48%, #111211 100%)';
    }
    var stops = [];
    for (var step = 0; step <= 8; step++) {
      var hue = range.start + ((range.end - range.start) * step / 8);
      stops.push('hsl(' + (((hue % 360) + 360) % 360) + ' 88% 54%) ' +
        Math.round(step / 8 * 100) + '%');
    }
    return 'linear-gradient(90deg, ' + stops.join(', ') + ')';
  }

  function familyControlValue(selection, range) {
    if (!range) return Number(selection.hue) || 0;
    if (range.neutral) {
      var rgb = hexRgb(selection.hex);
      var lightness = rgbHsl(rgb.r, rgb.g, rgb.b).l * 100;
      return clamp((96 - lightness) / 88 * 100, 0, 100);
    }
    return hueForFamilyRange(selection.hue, range);
  }

  function familySelectionFromValue(value, range) {
    var constrained = clamp(Number(value) || 0, range.start, range.end);
    var hex;
    var hue;
    if (range.neutral) {
      hex = hslHex(0, 0, 96 - (constrained / 100 * 88));
      hue = 0;
    } else {
      hue = ((constrained % 360) + 360) % 360;
      hex = hslHex(hue, 88, 54);
    }
    return { hue: hue, hex: hex, name: friendlyName(hex) };
  }

  function initialSelection(hex) {
    var value = /^#[0-9a-f]{6}$/i.test(String(hex || '')) ? String(hex).toUpperCase() : DEFAULT_HEX;
    return { hue: spectrumPosition(value) * 359, hex: value, name: friendlyName(value) };
  }

  function validHex(value) {
    return /^#[0-9a-f]{6}$/i.test(String(value || ''));
  }

  function familyForPageHandle(value) {
    var handle = String(value || '').trim().toLowerCase().replace(/_/g, '-');
    if (!handle) return '';
    var keys = Object.keys(COLOUR_FAMILY_HEX);
    for (var index = 0; index < keys.length; index++) {
      var key = keys[index];
      if (handle === key || handle === 'colour-' + key || handle === 'color-' + key ||
          handle === key + '-colour' || handle === key + '-color') return key;
    }
    return '';
  }

  function resolveColourContext(root) {
    var parameters = new URLSearchParams(window.location.search || '');
    var requested = parameters.get('colour_hex') || parameters.get('colour') || '';
    var productColour = root.getAttribute('data-product-colour') || '';
    var productFamily = String(root.getAttribute('data-product-colour-label') || '')
      .trim().toLowerCase();
    var scopedFinder = document.querySelector(
      '[data-pw-colour-finder][data-colour-family][data-initial-colour]'
    );
    var scopedFamily = scopedFinder
      ? String(scopedFinder.getAttribute('data-colour-family') || '').trim().toLowerCase()
      : '';
    // A product page is intentionally anchored to the garment's catalogue
    // colour. The spectrum remains interactive everywhere else, but here the
    // header acts as a direct, immediately available "shop this colour" route.
    if (root.getAttribute('data-product-context') === 'true' && validHex(productColour)) {
      return {
        active: true,
        contextual: true,
        product: true,
        family: productFamily,
        selection: initialSelection(productColour)
      };
    }
    if (validHex(requested)) {
      return {
        active: true,
        contextual: true,
        family: scopedFamily,
        selection: initialSelection(requested)
      };
    }

    // Colour tag pages already publish the exact family colour used by their
    // restricted picker. Read it directly so the header and page never drift.
    if (scopedFinder) {
      var scopedHex = scopedFinder.getAttribute('data-initial-colour') || '';
      if (validHex(scopedHex)) {
        return {
          active: true,
          contextual: true,
          family: scopedFamily,
          selection: initialSelection(scopedHex)
        };
      }
    }

    // Exact colour handles cover simple legacy pages that do not yet render
    // the family picker. Deliberately avoid partial matches such as a brand
    // called Red Herring.
    var family = familyForPageHandle(root.getAttribute('data-page-handle'));
    if (family && COLOUR_FAMILY_HEX[family]) {
      return {
        active: true,
        contextual: true,
        family: family,
        selection: initialSelection(COLOUR_FAMILY_HEX[family])
      };
    }
    return {
      active: false,
      contextual: false,
      family: '',
      selection: initialSelection(root.getAttribute('data-initial-colour'))
    };
  }

  function initialiseSpectrum(root) {
    if (!root || root.getAttribute('data-pwcs-ready') === 'true') return;
    var track = root.querySelector('[data-pwcs-track]');
    if (!track) return;
    root.setAttribute('data-pwcs-ready', 'true');

    var endpoint = root.getAttribute('data-count-map-url') || '';
    var resultsPath = root.getAttribute('data-results-url') || '/pages/find-your-colour';
    var mobilePanel = root.querySelector('[data-pwcs-mobile-panel]');
    var announcer = root.querySelector('[data-pwcs-announcer]');
    var photoDialog = document.getElementById(root.getAttribute('data-photo-dialog-id') || '');
    var photoPanel = photoDialog && photoDialog.querySelector('[data-pwcs-photo-panel]');
    var photoInput = document.querySelector('[data-pwcs-photo-file]');
    var photoContent = photoDialog && photoDialog.querySelector('[data-pwcs-photo-content]');
    var photoCanvas = photoDialog && photoDialog.querySelector('[data-pwcs-photo-canvas]');
    var photoStage = photoDialog && photoDialog.querySelector('[data-pwcs-photo-stage]');
    var photoMarker = photoDialog && photoDialog.querySelector('[data-pwcs-photo-marker]');
    var photoHint = photoDialog && photoDialog.querySelector('[data-pwcs-photo-hint]');
    var photoOptions = photoDialog && photoDialog.querySelector('[data-pwcs-photo-options]');
    var photoOptionList = photoDialog && photoDialog.querySelector('[data-pwcs-photo-option-list]');
    var photoSelectedSwatch = photoDialog && photoDialog.querySelector('[data-pwcs-photo-selected-swatch]');
    var photoSelectedName = photoDialog && photoDialog.querySelector('[data-pwcs-photo-selected-name]');
    var photoSelectedValue = photoDialog && photoDialog.querySelector('[data-pwcs-photo-selected-value]');
    var photoSelectedCoverage = photoDialog && photoDialog.querySelector('[data-pwcs-photo-selected-coverage]');
    var photoError = photoDialog && photoDialog.querySelector('[data-pwcs-photo-error]');
    var photoFooter = photoDialog && photoDialog.querySelector('[data-pwcs-photo-footer]');
    var photoView = photoDialog && photoDialog.querySelector('[data-pwcs-photo-view]');
    var mobileViewport = window.matchMedia('(max-width: 989px)');
    // Every enhanced product browser understands the shared exact-colour
    // event. Keep shoppers on their current collection/search scope instead
    // of routing them through the standalone colour page.
    var liveFinder = document.querySelector('#PrewornWideSearch');
    var colourContext = resolveColourContext(root);
    var productContext = root.getAttribute('data-product-context') === 'true';
    var productId = root.getAttribute('data-product-id') || '';
    var productHandle = root.getAttribute('data-product-handle') || '';
    var productResultsUrl = root.getAttribute('data-product-results-url') || '';
    var productColourLabel = root.getAttribute('data-product-colour-label') || '';
    var familyRange = productContext ? null : familyHueRange(colourContext.family);
    var locked = colourContext.selection;
    var visible = locked;
    var dragging = false;
    var pointerStart = null;
    var wasLockedBeforePointer = false;
    var frame = null;
    var queuedEvent = null;
    var countTimer = null;
    var countRequestId = 0;
    var countMap = readCountMap(endpoint);
    var photoState = {
      image: null,
      clusters: [],
      draw: null,
      marker: null,
      selectedHex: locked.hex,
      coverage: 0
    };
    var photoLastFocus = null;

    root.classList.add('is-enhanced');
    root.classList.toggle('is-product-context', productContext);
    root.classList.toggle('has-live-results', !!liveFinder);
    root.classList.toggle('is-family-range', !!familyRange);
    if (familyRange) {
      root.setAttribute('data-active-colour-family', colourContext.family);
      track.style.background = familyGradient(familyRange);
      track.setAttribute('aria-label', 'Choose a ' + colourContext.family + ' shade');
      track.setAttribute('aria-valuemin', String(familyRange.start));
      track.setAttribute('aria-valuemax', String(familyRange.end));
    }

    function countText(match) {
      if (!match) return 'Live count shown in results';
      return match.count.toLocaleString('en-GB') + (match.capped ? '+' : '') +
        (match.count === 1 ? ' matching piece' : ' matching pieces');
    }

    function renderCount(hex, text) {
      if (visible.hex !== hex && locked.hex !== hex) return;
      root.querySelectorAll('[data-pwcs-count]').forEach(function (element) {
        element.textContent = text;
      });
    }

    function scheduleCount(selection) {
      window.clearTimeout(countTimer);
      var requestId = ++countRequestId;
      var currentMatch = colourCountMapMatch(countMap, selection.hex);
      if (currentMatch) {
        renderCount(selection.hex, countText(currentMatch));
        return;
      }
      renderCount(selection.hex, 'Finding matches…');
      countTimer = window.setTimeout(function () {
        loadCountMap(endpoint).then(function (payload) {
          if (requestId !== countRequestId) return;
          countMap = payload;
          renderCount(selection.hex, countText(colourCountMapMatch(payload, selection.hex)));
        });
      }, COUNT_DEBOUNCE_MS);
    }

    function paint(selection, shouldLoadCount) {
      visible = selection;
      var controlValue = familyRange
        ? familyControlValue(selection, familyRange)
        : selection.hue;
      var position = familyRange
        ? clamp((controlValue - familyRange.start) /
          Math.max(1, familyRange.end - familyRange.start) * 100, 0, 100)
        : clamp(selection.hue / 359 * 100, 0, 100);
      root.style.setProperty('--pwcs-colour', selection.hex);
      root.style.setProperty('--pwcs-ink', contrastInk(selection.hex));
      if (productContext) root.style.setProperty('--pwcs-product-colour', selection.hex);
      root.style.setProperty('--pwcs-position', position + '%');
      track.setAttribute('aria-valuenow', String(Math.round(controlValue)));
      track.setAttribute('aria-valuetext', selection.name + ', ' + selection.hex);
      root.querySelectorAll('[data-pwcs-name]').forEach(function (element) {
        element.textContent = selection.name;
      });
      root.querySelectorAll('[data-pwcs-hex]').forEach(function (element) {
        element.textContent = selection.hex;
      });
      if (shouldLoadCount !== false) scheduleCount(selection);
    }

    function setShopActive(active, selection) {
      root.classList.toggle('is-locked', !!active);
      root.querySelectorAll('[data-pwcs-shop]').forEach(function (link) {
        link.setAttribute('aria-disabled', active ? 'false' : 'true');
        if (active) {
          link.removeAttribute('tabindex');
          link.setAttribute('href', productContext && productResultsUrl
            ? productResultsUrl : resultUrl(resultsPath, selection.hex));
          link.setAttribute('aria-label', productContext && productColourLabel
            ? 'Shop ' + productColourLabel + ' items'
            : 'Shop ' + selection.name + ' items');
        } else {
          link.setAttribute('tabindex', '-1');
        }
      });
    }

    function loadIndexedProductColour() {
      if (!productContext || !endpoint || (!productId && !productHandle) || !window.fetch) {
        return Promise.resolve(null);
      }
      var requestUrl;
      try {
        requestUrl = new URL(endpoint, window.location.origin);
      } catch (error) {
        return Promise.resolve(null);
      }
      requestUrl.searchParams.set('action', 'product_colour');
      if (productId) requestUrl.searchParams.set('product_id', productId);
      if (productHandle) requestUrl.searchParams.set('handle', productHandle);

      return window.fetch(requestUrl.toString(), {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'force-cache',
        headers: { Accept: 'application/json' }
      }).then(function (response) {
        if (!response.ok) throw new Error('Product colour unavailable');
        return response.json();
      }).then(function (payload) {
        var indexedHex = payload && validHex(payload.hex)
          ? String(payload.hex).toUpperCase() : '';
        if (!indexedHex) return null;

        var selection = initialSelection(indexedHex);
        productColourLabel = payload.name || selection.name;
        productResultsUrl = resultUrl(resultsPath, indexedHex);
        root.setAttribute('data-product-colour', indexedHex);
        root.setAttribute('data-product-colour-label', productColourLabel);
        root.setAttribute('data-product-colour-source', 'visual-index');
        root.style.setProperty('--pwcs-product-colour', indexedHex);
        root.style.setProperty('--pwcs-product-ink', contrastInk(indexedHex));
        root.querySelectorAll('[data-pwcs-product-name]').forEach(function (element) {
          element.textContent = productColourLabel;
        });
        root.querySelectorAll('[data-pwcs-product-hex]').forEach(function (element) {
          element.textContent = indexedHex;
        });
        root.querySelectorAll('[data-pwcs-marker]').forEach(function (element) {
          element.setAttribute('data-product-hex', indexedHex);
        });
        root.querySelectorAll('.pwcs__intro').forEach(function (link) {
          link.setAttribute('href', productResultsUrl);
        });
        applyResolvedContext({
          active: true,
          contextual: true,
          product: true,
          selection: selection
        });
        return payload;
      }).catch(function () {
        root.setAttribute('data-product-colour-source', 'tag-fallback');
        return null;
      });
    }

    function selectionAt(clientX) {
      var rectangle = track.getBoundingClientRect();
      var ratio = rectangle.width ? clamp((clientX - rectangle.left) / rectangle.width, 0, 1) : 0;
      if (familyRange) {
        return familySelectionFromValue(
          familyRange.start + ratio * (familyRange.end - familyRange.start),
          familyRange
        );
      }
      return selectionFromHue(ratio * 359);
    }

    function paintFromPointer(event) {
      paint(selectionAt(event.clientX));
      root.classList.add('is-previewing');
    }

    function queuePointer(event) {
      queuedEvent = event;
      if (frame !== null) return;
      frame = window.requestAnimationFrame(function () {
        frame = null;
        if (queuedEvent) paintFromPointer(queuedEvent);
        queuedEvent = null;
      });
    }

    function commit(selection) {
      locked = selection || visible;
      root.classList.remove('is-contextual');
      paint(locked);
      setShopActive(true, locked);
      if (liveFinder && typeof window.CustomEvent === 'function') {
        window.dispatchEvent(new CustomEvent('preworn:exact-colour-change', {
          detail: {
            hex: locked.hex,
            tolerance: 12,
            name: locked.name,
            source: 'header-spectrum'
          }
        }));
        if (announcer) announcer.textContent = locked.name + ' selected. Results are updating.';
        return;
      }
      if (mobileViewport.matches && mobilePanel) mobilePanel.hidden = false;
      if (announcer) announcer.textContent = locked.name + ' selected. Use View items to shop this shade.';
    }

    function clearPhotoError() {
      if (!photoError) return;
      photoError.textContent = '';
      photoError.hidden = true;
    }

    function showPhotoError(message) {
      if (!photoError) return;
      photoError.textContent = message;
      photoError.hidden = false;
    }

    function choosePhotoFile() {
      if (!photoInput) return;
      photoLastFocus = document.activeElement;
      photoInput.value = '';
      photoInput.click();
    }

    function openPhotoDialog(force) {
      if (!photoDialog || (!photoState.image && !force)) return;
      if (photoDialog.hidden && !photoLastFocus) photoLastFocus = document.activeElement;
      if (mobilePanel) mobilePanel.hidden = true;
      photoDialog.hidden = false;
      document.body.classList.add('pwcs-photo-open');
      window.requestAnimationFrame(function () {
        if (photoState.image) drawPhotoForDisplay(photoState.image);
        var closeButton = photoDialog.querySelector('[data-pwcs-photo-close]');
        if (!closeButton) return;
        try { closeButton.focus({ preventScroll: true }); }
        catch (error) { closeButton.focus(); }
      });
    }

    function closePhotoDialog(restoreFocus) {
      if (!photoDialog) return;
      photoDialog.hidden = true;
      document.body.classList.remove('pwcs-photo-open');
      var focusTarget = photoLastFocus;
      if (!focusTarget || (mobilePanel && mobilePanel.hidden && mobilePanel.contains(focusTarget))) {
        focusTarget = track;
      }
      if (restoreFocus !== false && focusTarget && typeof focusTarget.focus === 'function') {
        try { focusTarget.focus({ preventScroll: true }); }
        catch (error) { focusTarget.focus(); }
      }
      photoLastFocus = null;
    }

    function renderPhotoCoverage(coverage) {
      photoState.coverage = Math.max(0, Math.min(1, Number(coverage) || 0));
      if (!photoSelectedCoverage) return;
      photoSelectedCoverage.textContent = photoState.coverage > 0
        ? '· ' + Math.max(1, Math.round(photoState.coverage * 100)) + '% OF PHOTO'
        : '· <1% OF PHOTO';
    }

    function renderPhotoSelection(hex, coverage) {
      if (!validHex(hex)) return;
      photoState.selectedHex = hex.toUpperCase();
      if (photoSelectedSwatch) {
        photoSelectedSwatch.style.setProperty('--pwcs-photo-colour', photoState.selectedHex);
      }
      if (photoSelectedName) photoSelectedName.textContent = friendlyName(photoState.selectedHex);
      if (photoSelectedValue) photoSelectedValue.textContent = photoState.selectedHex;
      renderPhotoCoverage(coverage);
      if (photoOptionList) {
        photoOptionList.querySelectorAll('[data-pwcs-photo-option]').forEach(function (button) {
          button.classList.toggle(
            'is-active',
            String(button.getAttribute('data-pwcs-photo-option') || '').toUpperCase() === photoState.selectedHex
          );
        });
      }
    }

    function renderPhotoOptions(clusters) {
      if (!photoOptions || !photoOptionList) return;
      if (!clusters.length) {
        photoOptionList.innerHTML = '';
        photoOptions.hidden = true;
        return;
      }
      photoOptionList.innerHTML = clusters.map(function (cluster, index) {
        var name = friendlyName(cluster.hex);
        var coverage = Math.max(1, Math.min(100, Math.round(cluster.coverage * 100)));
        return '<button type="button" data-pwcs-photo-option="' + cluster.hex + '" ' +
          'data-photo-x="' + cluster.x + '" data-photo-y="' + cluster.y + '" ' +
          'data-photo-coverage="' + cluster.coverage + '" ' +
          'aria-label="Select ' + escapeHtml(name) + ', ' + coverage +
          ' percent of photo, ' + cluster.hex + '">' +
            '<i style="--pwcs-photo-option:' + cluster.hex + '"></i>' +
            '<span><strong>' + escapeHtml(name) + '</strong>' +
            '<small>' + cluster.hex + ' · ' + coverage + '%' +
            (index === 0 ? ' · Dominant' : '') + '</small></span>' +
            '<b>' + coverage + '%</b>' +
          '</button>';
      }).join('');
      photoOptions.hidden = false;
    }

    function nearestPhotoCoverage(hex) {
      if (!validHex(hex) || !photoState.clusters.length) return 0;
      var rgb = hexRgb(hex);
      var targetLab = rgbLab(rgb.r, rgb.g, rgb.b);
      var nearest = null, nearestDistance = Infinity;
      photoState.clusters.forEach(function (cluster) {
        var clusterRgb = hexRgb(cluster.hex);
        var distance = labDistance(targetLab, rgbLab(clusterRgb.r, clusterRgb.g, clusterRgb.b));
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = cluster;
        }
      });
      return nearest ? nearest.coverage : 0;
    }

    function sizePhotoStage(image) {
      if (!photoStage || !photoContent || !image) return;
      var contentStyle = window.getComputedStyle(photoContent);
      var padding = (parseFloat(contentStyle.paddingLeft) || 0) +
        (parseFloat(contentStyle.paddingRight) || 0);
      var availableWidth = Math.max(1, photoContent.clientWidth - padding);
      var aspect = Math.max(.1, image.naturalWidth / Math.max(1, image.naturalHeight));
      var compact = window.matchMedia && window.matchMedia('(max-width: 700px)').matches;
      var heightLimit = compact
        ? Math.min(220, Math.max(110, window.innerHeight * .24))
        : Math.min(360, Math.max(160, window.innerHeight * .34));
      var height = Math.min(availableWidth / aspect, heightLimit);
      var width = Math.min(availableWidth, height * aspect);
      photoStage.style.width = Math.max(1, Math.round(width)) + 'px';
      photoStage.style.height = Math.max(1, Math.round(height)) + 'px';
    }

    function drawPhotoForDisplay(image) {
      if (!photoCanvas || !photoStage || !image) return;
      sizePhotoStage(image);
      var rectangle = photoStage.getBoundingClientRect();
      var cssWidth = Math.max(1, Math.round(rectangle.width || 700));
      var cssHeight = Math.max(1, Math.round(rectangle.height || 300));
      var pixelRatio = Math.max(1, Math.min(2, Number(window.devicePixelRatio) || 1));
      var width = Math.min(1000, Math.round(cssWidth * pixelRatio));
      var height = Math.max(1, Math.round(width * cssHeight / cssWidth));
      photoCanvas.width = width;
      photoCanvas.height = height;
      var context = photoCanvas.getContext('2d', { willReadFrequently: true });
      context.clearRect(0, 0, width, height);
      var scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
      var drawWidth = Math.max(1, Math.round(image.naturalWidth * scale));
      var drawHeight = Math.max(1, Math.round(image.naturalHeight * scale));
      var drawX = Math.round((width - drawWidth) / 2);
      var drawY = Math.round((height - drawHeight) / 2);
      context.drawImage(image, drawX, drawY, drawWidth, drawHeight);
      photoState.draw = { x: drawX, y: drawY, width: drawWidth, height: drawHeight };
      positionPhotoMarker(photoState.marker);
    }

    function positionPhotoMarker(marker) {
      photoState.marker = marker || null;
      if (!photoMarker || !photoCanvas || !photoState.marker || !photoState.draw) {
        if (photoMarker) photoMarker.hidden = true;
        return;
      }
      var rectangle = photoCanvas.getBoundingClientRect();
      var draw = photoState.draw;
      var internalX = draw.x + clamp(Number(photoState.marker.x) || 0, 0, 1) * draw.width;
      var internalY = draw.y + clamp(Number(photoState.marker.y) || 0, 0, 1) * draw.height;
      photoMarker.style.left = (internalX * rectangle.width / photoCanvas.width) + 'px';
      photoMarker.style.top = (internalY * rectangle.height / photoCanvas.height) + 'px';
      photoMarker.style.background = photoState.marker.hex;
      photoMarker.hidden = false;
    }

    function samplePhoto(image) {
      var analysis = document.createElement('canvas');
      var scale = Math.min(1, 200 / Math.max(image.naturalWidth, image.naturalHeight));
      analysis.width = Math.max(1, Math.round(image.naturalWidth * scale));
      analysis.height = Math.max(1, Math.round(image.naturalHeight * scale));
      analysis.getContext('2d', { willReadFrequently: true }).drawImage(
        image, 0, 0, analysis.width, analysis.height
      );
      return dominantPhotoColours(analysis);
    }

    function usePhotoFile(file) {
      if (!file || !/^image\/(?:png|jpeg|webp)$/i.test(file.type || '') ||
          file.size > 12 * 1024 * 1024) {
        showPhotoError('Choose a JPG, PNG or WebP image smaller than 12 MB.');
        openPhotoDialog(true);
        return;
      }
      clearPhotoError();
      var reader = new FileReader();
      reader.onload = function () {
        var image = new Image();
        image.onload = function () {
          var clusters = samplePhoto(image);
          photoState.image = image;
          photoState.clusters = clusters;
          photoState.marker = clusters.length ? {
            x: clusters[0].x,
            y: clusters[0].y,
            hex: clusters[0].hex
          } : null;
          if (photoContent) photoContent.hidden = false;
          if (photoFooter) photoFooter.hidden = false;
          if (photoHint) {
            photoHint.textContent = clusters.length
              ? 'Tap the photo to pick another colour.'
              : 'Tap the photo to pick a colour.';
          }
          renderPhotoOptions(clusters);
          renderPhotoSelection(
            clusters.length ? clusters[0].hex : locked.hex,
            clusters.length ? clusters[0].coverage : 0
          );
          openPhotoDialog();
        };
        image.onerror = function () {
          showPhotoError('That image could not be opened. Try another photo.');
          openPhotoDialog(true);
        };
        image.src = reader.result;
      };
      reader.onerror = function () {
        showPhotoError('That image could not be opened. Try another photo.');
        openPhotoDialog(true);
      };
      reader.readAsDataURL(file);
    }

    track.addEventListener('pointerenter', function (event) {
      if (productContext) return;
      if (event.pointerType === 'mouse' && !root.classList.contains('is-locked')) {
        root.classList.add('is-previewing');
        queuePointer(event);
      }
    });

    track.addEventListener('pointermove', function (event) {
      if (productContext) return;
      if (!dragging && event.pointerType !== 'mouse') return;
      if (!dragging && root.classList.contains('is-locked')) return;
      if (dragging && event.pointerType === 'touch' && pointerStart) {
        var horizontal = Math.abs(event.clientX - pointerStart.x);
        var vertical = Math.abs(event.clientY - pointerStart.y);
        if (vertical > horizontal && vertical > 8) return;
      }
      queuePointer(event);
    });

    track.addEventListener('pointerleave', function () {
      if (productContext) return;
      if (dragging) return;
      if (root.classList.contains('is-locked')) {
        paint(locked);
      } else {
        root.classList.remove('is-previewing');
        paint(locked, false);
      }
    });

    track.addEventListener('pointerdown', function (event) {
      if (productContext) return;
      dragging = true;
      wasLockedBeforePointer = root.classList.contains('is-locked');
      root.classList.remove('is-contextual');
      setShopActive(false, locked);
      pointerStart = { x: event.clientX, y: event.clientY, type: event.pointerType };
      if (event.pointerType !== 'touch') event.preventDefault();
      if (track.setPointerCapture && event.pointerType !== 'touch') {
        try { track.setPointerCapture(event.pointerId); } catch (error) {}
      }
      if (event.pointerType !== 'touch') paintFromPointer(event);
    });

    track.addEventListener('pointerup', function (event) {
      if (productContext) return;
      if (!dragging) return;
      var horizontal = pointerStart ? Math.abs(event.clientX - pointerStart.x) : 0;
      var vertical = pointerStart ? Math.abs(event.clientY - pointerStart.y) : 0;
      dragging = false;
      if (event.pointerType === 'touch' && vertical > horizontal && vertical > 10) {
        paint(locked);
        setShopActive(wasLockedBeforePointer, locked);
        return;
      }
      var selection = selectionAt(event.clientX);
      paint(selection);
      commit(selection);
    });

    track.addEventListener('pointercancel', function () {
      if (productContext) return;
      dragging = false;
      paint(locked);
      setShopActive(wasLockedBeforePointer, locked);
    });

    track.addEventListener('focus', function () {
      if (productContext) return;
      root.classList.add('is-previewing');
      paint(locked);
    });

    track.addEventListener('blur', function () {
      if (productContext) return;
      if (root.classList.contains('is-locked')) {
        paint(locked);
      } else {
        root.classList.remove('is-previewing');
        paint(locked, false);
      }
    });

    track.addEventListener('keydown', function (event) {
      if (productContext) return;
      var supported = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter', ' '];
      if (supported.indexOf(event.key) === -1) return;
      event.preventDefault();
      if (event.key === 'Enter' || event.key === ' ') {
        commit(visible);
        return;
      }
      setShopActive(false, locked);
      var nextHue = familyRange
        ? familyControlValue(visible, familyRange)
        : visible.hue;
      var step = event.shiftKey ? 10 : 2;
      if (event.key === 'ArrowLeft') nextHue -= step;
      else if (event.key === 'ArrowRight') nextHue += step;
      else if (event.key === 'Home') nextHue = familyRange ? familyRange.start : 0;
      else if (event.key === 'End') nextHue = familyRange ? familyRange.end : 359;
      paint(familyRange
        ? familySelectionFromValue(nextHue, familyRange)
        : selectionFromHue(clamp(nextHue, 0, 359)));
      root.classList.add('is-previewing');
    });

    root.querySelectorAll('[data-pwcs-close]').forEach(function (button) {
      button.addEventListener('click', function () {
        if (mobilePanel) mobilePanel.hidden = true;
        track.focus();
      });
    });

    root.querySelectorAll('[data-pwcs-shop]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        if (link.getAttribute('aria-disabled') === 'true') event.preventDefault();
      });
    });

    root.querySelectorAll('[data-pwcs-photo-open]').forEach(function (button) {
      button.addEventListener('click', choosePhotoFile);
    });
    if (photoInput) {
      photoInput.addEventListener('change', function () {
        usePhotoFile(this.files && this.files[0]);
      });
    }
    if (photoDialog) {
      photoDialog.querySelectorAll('[data-pwcs-photo-close]').forEach(function (button) {
        button.addEventListener('click', function () { closePhotoDialog(true); });
      });
      photoDialog.querySelectorAll('[data-pwcs-photo-replace]').forEach(function (button) {
        button.addEventListener('click', choosePhotoFile);
      });
      photoDialog.addEventListener('click', function (event) {
        if (event.target === photoDialog) closePhotoDialog(true);
      });
      photoDialog.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          closePhotoDialog(true);
          return;
        }
        if (event.key !== 'Tab' || !photoPanel) return;
        var focusable = Array.prototype.slice.call(
          photoPanel.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])')
        ).filter(function (element) {
          return !element.disabled && element.offsetParent !== null;
        });
        if (!focusable.length) return;
        var first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }
    if (photoOptionList) {
      photoOptionList.addEventListener('click', function (event) {
        var button = event.target.closest('[data-pwcs-photo-option]');
        if (!button || !photoOptionList.contains(button)) return;
        var hex = button.getAttribute('data-pwcs-photo-option');
        positionPhotoMarker({
          x: Number(button.getAttribute('data-photo-x')),
          y: Number(button.getAttribute('data-photo-y')),
          hex: hex
        });
        renderPhotoSelection(hex, Number(button.getAttribute('data-photo-coverage')));
      });
    }
    if (photoCanvas) {
      photoCanvas.addEventListener('click', function (event) {
        var rectangle = photoCanvas.getBoundingClientRect();
        var x = clamp(
          Math.floor((event.clientX - rectangle.left) * photoCanvas.width / rectangle.width),
          0,
          photoCanvas.width - 1
        );
        var y = clamp(
          Math.floor((event.clientY - rectangle.top) * photoCanvas.height / rectangle.height),
          0,
          photoCanvas.height - 1
        );
        var pixel = photoCanvas.getContext('2d', { willReadFrequently: true })
          .getImageData(x, y, 1, 1).data;
        if (pixel[3] < 35) {
          if (photoHint) photoHint.textContent = 'Tap the visible photo to select a colour.';
          return;
        }
        var selectedHex = rgbHex(pixel[0], pixel[1], pixel[2]);
        var draw = photoState.draw || {
          x: 0,
          y: 0,
          width: photoCanvas.width,
          height: photoCanvas.height
        };
        positionPhotoMarker({
          x: clamp((x - draw.x) / Math.max(1, draw.width), 0, 1),
          y: clamp((y - draw.y) / Math.max(1, draw.height), 0, 1),
          hex: selectedHex
        });
        if (photoHint) photoHint.textContent = 'Custom colour selected from your photo.';
        renderPhotoSelection(selectedHex, nearestPhotoCoverage(selectedHex));
      });
    }
    if (photoView) {
      photoView.addEventListener('click', function () {
        if (liveFinder) {
          closePhotoDialog(false);
          commit(initialSelection(photoState.selectedHex));
          return;
        }
        window.location.assign(resultUrl(resultsPath, photoState.selectedHex));
      });
    }
    window.addEventListener('resize', function () {
      if (!photoState.image || !photoDialog || photoDialog.hidden) return;
      drawPhotoForDisplay(photoState.image);
    });

    function applyResolvedContext(nextContext) {
      if (!nextContext || !nextContext.selection) return;
      locked = nextContext.selection;
      visible = locked;
      root.classList.toggle('is-contextual', !!nextContext.contextual);
      root.classList.remove('is-previewing');
      paint(locked, false);
      setShopActive(!!nextContext.active, locked);
    }

    function applyExternalSelection(hex) {
      if (!validHex(hex)) return;
      applyResolvedContext({
        active: true,
        contextual: true,
        selection: initialSelection(hex)
      });
    }

    function syncExactColourEvent(event) {
      var detail = event && event.detail ? event.detail : {};
      if (validHex(detail.hex)) {
        applyExternalSelection(detail.hex);
      } else if (detail.preservePicker) {
        // Named catalogue colours change the product filter only. Their
        // representative colour positions the visual marker without turning
        // the catalogue request back into an exact HEX search.
        if (validHex(detail.pickerHex)) {
          applyResolvedContext({
            active: true,
            contextual: true,
            selection: initialSelection(detail.pickerHex)
          });
        }
        return;
      } else {
        window.setTimeout(function () {
          applyResolvedContext(resolveColourContext(root));
        }, 0);
      }
    }

    // `change` is the shopper's request; `state` is the product browser's
    // confirmed value. Listening to both keeps the header marker aligned when
    // colour changes originate in the shade field, presets, HEX input, photo
    // picker or eyedropper rather than in this header itself.
    window.addEventListener('preworn:exact-colour-change', syncExactColourEvent);
    window.addEventListener('preworn:exact-colour-state', syncExactColourEvent);

    window.addEventListener('popstate', function () {
      applyResolvedContext(resolveColourContext(root));
    });

    // Keep first paint network-free. The shared count map is requested only
    // after hover, focus, drag or tap demonstrates colour-finding intent.
    applyResolvedContext(colourContext);
    loadIndexedProductColour();
  }

  function initialiseAll(scope) {
    var owner = scope && scope.querySelectorAll ? scope : document;
    owner.querySelectorAll('[data-pw-colour-spectrum]').forEach(initialiseSpectrum);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initialiseAll(document); });
  } else {
    initialiseAll(document);
  }
  document.addEventListener('shopify:section:load', function (event) {
    initialiseAll(event.target);
  });
})();
